import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL

export type PrizeDistribution = {
  totalPrizes: number
  windowStart: string
  windowEnd: string
  bucketSeconds: number
  abandonmentTimeoutSeconds?: number
}

export type RoundDef = {
  id: string
  name?: string | null
  startDate: string
  endDate: string
  /** Optional per-round cap on attempts per unique user. Absent = unlimited. */
  maxAttempts?: number | null
  /** Optional prize-distribution config for the round. Absent = unlimited prizes. */
  prizeDistribution?: PrizeDistribution | null
}

export type ExplicitRoundSchedule = {
  scheduleType: 'explicit-rounds'
  campaignStartDate?: string | null
  campaignEndDate?: string | null
  rounds: RoundDef[]
  killSwitch?: boolean
  completionMessage?: string | null
}

export type RoundScheduleResponse =
  | { scheduleType: 'explicit-rounds'; periodicSchedule: ExplicitRoundSchedule }
  | { scheduleType: 'periodic'; reason: 'periodic-not-explicit' }
  | { scheduleType: 'none'; reason: 'no-schedule' }

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString()
  if (!token) {
    throw new Error('You must be logged in to perform this action.')
  }
  return { Authorization: `Bearer ${token}` }
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const code: string | undefined = error.response?.data?.code
    const msg: string | undefined = error.response?.data?.message
    if (code === 'NOT_EXPLICIT_ROUNDS') {
      return 'This campaign does not use the explicit-rounds schedule.'
    }
    if (code === 'ROUND_NOT_FOUND') {
      return msg || 'A round you tried to update was not found.'
    }
    if (code === 'INVALID_ROUND_DATES') {
      return msg || 'A round has an end date that is not after its start date.'
    }
    if (code === 'OVERLAPPING_ROUNDS') {
      return msg || 'Rounds cannot overlap. Adjust the dates.'
    }
    if (code === 'MISSING_ROUNDS') {
      return 'No rounds were provided to save.'
    }
    if (msg) return msg
  }
  return fallback
}

export const roundsService = {
  /**
   * Fetch the current round schedule from the pack JSON.
   * Returns shape variants:
   *   { scheduleType: 'explicit-rounds', periodicSchedule: {...} } — editable in modal
   *   { scheduleType: 'periodic',         reason: 'periodic-not-explicit' } — show "edit in library" link
   *   { scheduleType: 'none',             reason: 'no-schedule' } — no rounds at all
   */
  async getRoundSchedule(campaignId: string): Promise<RoundScheduleResponse> {
    const headers = await getAuthHeaders()
    try {
      const response = await axios.get<{ success: boolean; data: RoundScheduleResponse }>(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/rounds`,
        { headers },
      )
      return response.data.data
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return { scheduleType: 'none', reason: 'no-schedule' }
      }
      throw new Error(extractErrorMessage(err, 'Failed to load round schedule.'))
    }
  },

  /**
   * Patch the start/end dates of existing rounds. Cannot add or remove rounds.
   * Backend validates non-overlap after sort by startDate.
   * Returns the updated schedule on success.
   */
  async patchRoundSchedule(
    campaignId: string,
    rounds: Array<{ id: string; startDate: string; endDate: string; maxAttempts?: number | null; prizeDistribution?: PrizeDistribution | null }>,
  ): Promise<ExplicitRoundSchedule> {
    const headers = await getAuthHeaders()
    try {
      const response = await axios.post<{
        success: boolean
        data: { periodicSchedule: ExplicitRoundSchedule }
      }>(
        `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/rounds`,
        { rounds },
        { headers },
      )
      return response.data.data.periodicSchedule
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to save round schedule.'))
    }
  },
}
